/* eslint-disable no-console */
import auth0 from 'auth0-js';
import JwtDecode from 'jwt-decode';
import CONFIG from 'Config/settings';
import Routes from 'Routes/profileRoutes';
import {
  writeAuthTokenCookie,
  getCodeFromQueryStrings,
  validateIdToken,
  deleteRefreshToken,
  getCoworkerLinkingStatusFromQueryStrings,
  getUtmCampaignParams,
  getItmCampaignParams,
  isRememberMeSelected,
  isInsideIframe
} from 'Utils/auth/authUtils';
import analytics from 'Utils/analytics/analyticsWrapper';
import crypto from 'crypto-js';

const icwLinkValues = ['already-linked', 'linked'];

const webAuth = new auth0.WebAuth({
  domain: CONFIG.API.AUTH0.DOMAIN,
  clientID: CONFIG.API.AUTH0.CLIENT_ID
});

const base64URLEncode = wordArray => {
  return crypto.enc.Base64.stringify(wordArray)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

const sha256 = buffer => {
  return crypto.algo.SHA256.create()
    .update(buffer)
    .finalize();
};

const getChallenge = () => {
  const verifier = base64URLEncode(crypto.lib.WordArray.random(32));
  localStorage.setItem(`verifier_${CONFIG.COUNTRY.toLowerCase()}`, verifier);
  return base64URLEncode(sha256(verifier));
};

const login = async (redirectTo, bvEventId, icw) => {
  const { search } = window.location;
  const utmCampaignValues = getUtmCampaignParams(search);
  const itmCampaignValues = redirectTo?.includes(CONFIG.APP.BASE_NAME)
    ? 'customer-project|slider|login'
    : getItmCampaignParams(search);
  const codeChallenge = getChallenge();
  const authorizePayload = {
    redirectUri: Routes.LOGIN,
    responseType: 'code', // code for authorization code flow
    ui_locales: `${CONFIG.LANGUAGE}-${CONFIG.COUNTRY.toUpperCase()}`,
    codeChallenge,
    codeChallengeMethod: 'S256',
    scope: 'openid profile email offline_access',
    ...(redirectTo && {
      state: JSON.stringify(`redirectTo=${redirectTo}`)
    }),
    ...(bvEventId && {
      state: JSON.stringify(`bveventid=${bvEventId}`)
    }),
    ...(icw && {
      state: JSON.stringify(`icw=${icw}`)
    }),
    audience: 'https://retail.api.ikea.com',
    ...(utmCampaignValues &&
      utmCampaignValues !== '||' && {
        campaignSource: utmCampaignValues
      }),
    ...(itmCampaignValues &&
      itmCampaignValues !== '||' && {
        itmSource: itmCampaignValues
      }),
    registration: JSON.stringify({
      from: redirectTo,
      bveventid: bvEventId
    }),
    consumer: 'OWF'
  };
  if (isInsideIframe()) {
    await analytics.ciePagePopup('login_form', 'login_page_popup_redirect');
    webAuth.popup.authorize(authorizePayload);
  } else {
    webAuth.authorize(authorizePayload);
  }
};

const logout = async (redirectLoggedOutTo, accessToken) => {
  const token = localStorage.getItem(
    `refreshToken_${CONFIG.COUNTRY.toLowerCase()}`
  );
  if (token) {
    await fetch(`https://${CONFIG.API.AUTH0.DOMAIN}/oauth/revoke`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: CONFIG.API.AUTH0.CLIENT_ID,
        token
      })
    });
    deleteRefreshToken();
  } else {
    try {
      const userId = JwtDecode(accessToken).sub;
      if (window.ikea && window.ikea.cie) {
        await window.ikea.cie.deleteRefreshToken({
          country: CONFIG.COUNTRY.toLowerCase(),
          lang: CONFIG.LANGUAGE.toLowerCase(),
          userId,
          accessToken
        });
      }
    } catch {
      // Ignore errors while deleting refresh token
    }
  }
  webAuth.logout({
    clientID: CONFIG.API.AUTH0.CLIENT_ID,
    returnTo: `${Routes.LOGOUT}?from=${redirectLoggedOutTo}`
  });
};

const handleAuthentication = async queryStrings => {
  const code = getCodeFromQueryStrings(queryStrings);
  const icowLinked = getCoworkerLinkingStatusFromQueryStrings(queryStrings);
  if (!code && !icowLinked) {
    throw new Error('No exchange code received!');
  }

  if (code) {
    const bodyData = {
      grant_type: 'authorization_code',
      client_id: CONFIG.API.AUTH0.CLIENT_ID,
      code_verifier: localStorage.getItem(
        `verifier_${CONFIG.COUNTRY.toLowerCase()}`
      ),
      code,
      redirect_uri: Routes.LOGIN,
      scope: 'openid profile email offline_access'
    };
    const authResult = await fetchTokens(bodyData, false);
    if (authResult.refresh_token) {
      if (isRememberMeSelected(authResult.id_token)) {
        let userId = '';
        try {
          userId = JwtDecode(authResult.access_token).sub;
          if (window.ikea && window.ikea.cie) {
            await window.ikea.cie.saveRefreshToken({
              country: CONFIG.COUNTRY.toLowerCase(),
              lang: CONFIG.LANGUAGE.toLowerCase(),
              userId,
              accessToken: authResult.access_token,
              refreshToken: authResult.refresh_token
            });
          }
        } catch {
          // Ignore errors while saving refresh token
        }
      } else {
        await fetch(`https://${CONFIG.API.AUTH0.DOMAIN}/oauth/revoke`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            client_id: CONFIG.API.AUTH0.CLIENT_ID,
            token: authResult.refresh_token
          })
        });
      }
    }
    localStorage.removeItem(`verifier_${CONFIG.COUNTRY.toLowerCase()}`);
    return authResult;
  }

  if (icwLinkValues.includes(icowLinked)) {
    return refreshToken();
  }

  return undefined;
};

const refreshToken = async () => {
  return new Promise((resolve, reject) => {
    webAuth.checkSession(
      {
        redirectUri: Routes.LOGIN,
        responseType: 'token id_token',
        audience: 'https://retail.api.ikea.com',
        scope: 'openid profile email offline_access'
      },
      async function handleCheckSessionResult(err, authResult) {
        if (err) {
          console.log('Silent authentication error....', err);
          return reject(err);
        }
        if (
          authResult &&
          authResult.accessToken &&
          validateIdToken(authResult.idToken)
        ) {
          if (authResult.refresh_token) {
            let userId = '';
            try {
              userId = JwtDecode(authResult.accessToken).sub;
              if (window.ikea && window.ikea.cie) {
                await window.ikea.cie.saveRefreshToken({
                  country: CONFIG.COUNTRY.toLowerCase(),
                  lang: CONFIG.LANGUAGE.toLowerCase(),
                  userId,
                  accessToken: authResult.accessToken,
                  refreshToken: authResult.refresh_token
                });
              }
            } catch {
              // Ignore errors while saving refresh token
            }
          }
          await writeAuthTokenCookie(authResult.accessToken);
          return resolve(authResult);
        }
        console.log('Silent authentication Failed.');
        return reject(new Error('Invalid Token'));
      }
    );
  });
};

const fetchTokens = (bodyData, isRememberMe) => {
  return fetch(`https://${CONFIG.API.AUTH0.DOMAIN}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(bodyData)
  })
    .then(res => res.json())
    .then(async authResult => {
      if (
        authResult &&
        authResult.access_token &&
        validateIdToken(authResult.id_token)
      ) {
        await writeAuthTokenCookie(authResult.access_token);
        return authResult;
      }
      if (isRememberMe) {
        await deleteRefreshToken();
      }

      throw new Error('Invalid Token!');
    })
    .catch(() => {
      throw new Error('Exchange code failed!');
    });
};

export default {
  login,
  logout,
  handleAuthentication,
  refreshToken
};
